import { Key } from '~/constants/keyboard'
import { getStaticUrl } from '~/utils/static'

export function elementIsOverflownInParent(
  el: HTMLElement,
  parentEl: HTMLElement
) {
  let overflowDirection = 'up'

  const overflowBottom =
    el.offsetTop + el.offsetHeight > parentEl.clientHeight + parentEl.scrollTop

  const overflowTop = el.offsetTop < parentEl.scrollTop

  if (overflowBottom || overflowTop) {
    if (overflowBottom) {
      overflowDirection = 'down'
    }
    return overflowDirection
  }

  return false
}

export function elementIsHorizontallyOverflownInParent(
  el: HTMLElement,
  parentEl: HTMLElement,
  epsilon = 0.5 // tolerance for sub-pixel rounding
) {
  const elRect = el.getBoundingClientRect()
  const parentRect = parentEl.getBoundingClientRect()

  const overflowLeft = elRect.left < parentRect.left - epsilon
  const overflowRight = elRect.right > parentRect.right + epsilon

  if (overflowLeft || overflowRight) {
    return overflowRight ? 'right' : 'left'
  }
  return false
}

export function isElementInViewport(
  element: HTMLElement,
  partiallyVisible = false
): boolean {
  const { top, left, bottom, right } = element.getBoundingClientRect()
  const { innerHeight, innerWidth } = window

  if (!partiallyVisible) {
    return top >= 0 && left >= 0 && bottom <= innerHeight && right <= innerWidth
  }

  return (
    (top > 0 && top < innerHeight) ||
    (bottom > 0 && bottom < innerHeight) ||
    (left > 0 && left < innerWidth) ||
    (right > 0 && right < innerWidth)
  )
}

export function copyTextToClipboard(text: string) {
  const el = document.createElement('textarea')
  el.value = text
  el.id = 'copy_dummy'
  el.setAttribute('readonly', '')
  el.style.position = 'absolute'
  el.style.left = '-9999px'
  document.body.appendChild(el)
  el.select()
  document.execCommand('copy')
  document.body.removeChild(el)
}

export function imageToDataURL(img: ImageBitmap) {
  const canvas = document.createElement('canvas')
  canvas.width = img.width
  canvas.height = img.height

  const ctx = canvas.getContext('2d')
  ctx && ctx.drawImage(img, 0, 0)

  return canvas.toDataURL('image/png')
}

export function elementIsVisibleInViewport(el: HTMLElement | null) {
  if (!el) {
    return
  }
  const rect = el.getBoundingClientRect()

  return (
    rect.bottom >= 0 &&
    rect.right >= 0 &&
    rect.top <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.left <= (window.innerWidth || document.documentElement.clientWidth)
  )
}

export function changeFaviconNotification(hasNotifications: boolean) {
  if (process.server) {
    return
  }

  const notificationFavicon =
    'https://static.car.gr/static/favicon/favicon_notification.ico?v=2'
  const originalFavicon = 'https://static.car.gr/static/favicon/favicon.ico?v=2'

  if (hasNotifications) {
    changeFavicon(notificationFavicon)
    const ping = new Audio(getStaticUrl('/nuxt-static/audio/ping.mp3'))
    try {
      ping.play()
    } catch (_e) {}
  } else {
    changeFavicon(originalFavicon)
  }
}

function changeFavicon(src: string) {
  const link = document.createElement('link')
  const oldLink = document.getElementById('favicon')
  link.id = 'favicon'
  link.rel = 'shortcut icon'
  link.href = src
  if (oldLink) {
    document.head.removeChild(oldLink)
  }
  document.head.appendChild(link)
}

export function downloadFile(
  fileName: string,
  content: any,
  mimeType = 'text/plain'
) {
  const blob = new Blob([content], { type: mimeType })
  const a = document.createElement('a')
  const url = URL.createObjectURL(blob)
  a.setAttribute('href', url)
  a.setAttribute('download', fileName)
  a.click()
  URL.revokeObjectURL(url)
}

export function elementLargerThanScreen(elem: HTMLElement): boolean {
  if (process.client && elem?.offsetHeight) {
    let windowHeight = window.innerHeight
    const header = document.getElementById('header-section')
    if (header) {
      windowHeight -= header.offsetHeight
    }
    const elemHeight =
      elem.offsetHeight +
      parseInt(window.getComputedStyle(elem).getPropertyValue('margin-top')) +
      parseInt(window.getComputedStyle(elem).getPropertyValue('margin-bottom'))
    return elemHeight > windowHeight
  }
  return true // so it defaults to top
}

export function isElementVisible(el: HTMLElement) {
  const style = window.getComputedStyle(el)
  if (
    style.display === 'none' ||
    style.visibility === 'hidden' ||
    style.opacity === '0'
  ) {
    return false
  }
  const rect = el.getBoundingClientRect()
  return (
    rect.top < window.innerHeight &&
    rect.bottom > 0 &&
    rect.left < window.innerWidth &&
    rect.right > 0
  )
}

export function preventNonNumericKeyboardEvent(e: KeyboardEvent): void {
  if (e.isComposing || e.keyCode === 229) {
    return
  }
  if (
    // Some generic keys are allowed
    [
      Key.DELETE,
      Key.BACKSPACE,
      Key.ESCAPE,
      Key.ENTER,
      Key.END,
      Key.HOME,
      Key.ARROW_LEFT,
      Key.ARROW_RIGHT
    ].includes(e.key as Key) ||
    // Allow Ctrl + A
    ((e.key === Key.A || e.key === Key.A_CAPS) && (e.ctrlKey || e.metaKey)) ||
    // Allow Ctrl + X
    ((e.key === Key.X || e.key === Key.X_CAPS) && (e.ctrlKey || e.metaKey))
  ) {
    return
  }
  // Ensure that it is a number and stop the keypress
  if (
    (e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
    (e.keyCode < 96 || e.keyCode > 105)
  ) {
    e.preventDefault()
  }
}
